import {
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useMediaQuery,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { surepassTheme } from '../../theme/surepass';
import type { Checkpoint } from '../../networkRequest/checkpointsService';
import type { Location } from '../../networkRequest/locationsService';

/** "1 checkpoint", "3 checkpoints", "2 Athena checkpoints". */
const countCheckpoints = (count: number, kind = '') => {
  let noun = 'checkpoints';
  if (count === 1) {
    noun = 'checkpoint';
  }
  return [count, kind, noun].filter(Boolean).join(' ');
};

/** "Athena", "Athena and Genetec", "Athena, Genetec and Evolv". */
const joinNames = (names: string[]): string => {
  let joined = names.join('');
  if (names.length > 1) {
    joined = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  return joined;
};

/**
 * Why the location can't be deleted and what to do about it. The title already
 * names the location, so the body says "its".
 */
const getMessage = (partnerCheckpoints: Checkpoint[]): string => {
  const partnerNames = [
    ...new Set(partnerCheckpoints.flatMap((checkpoint) => (checkpoint.partner ? [checkpoint.partner.name] : []))),
  ];
  const checkpoints = countCheckpoints(partnerCheckpoints.length, joinNames(partnerNames));
  return `Only a SurePass administrator can delete its ${checkpoints}. Contact your SurePass administrator for help.`;
};

type CannotDeleteLocationDialogProps = {
  open: boolean;
  location: Location;
  /** The location's partner checkpoints. There's at least one, or the location could be deleted. */
  partnerCheckpoints: Checkpoint[];
  onClose: () => void;
};

/**
 * Shown instead of the delete confirmation while a location has partner
 * checkpoints: deleting a location deletes its checkpoints, and only a SurePass
 * administrator can delete a partner's. There's
 * nothing to decide, so the one button is a plain dismiss (Button page: Cancel /
 * dismiss is text, inherit).
 */
const CannotDeleteLocationDialog = ({ open, location, partnerCheckpoints, onClose }: CannotDeleteLocationDialogProps) => {
  // Below sm the dialog fills the screen (Dialog page). `fullScreen` is a prop, so it needs the hook.
  const isMobile = useMediaQuery(surepassTheme.breakpoints.down('sm'));
  const titleId = `cannot-delete-location-${location.id}-title`;
  const descriptionId = `cannot-delete-location-${location.id}-description`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      {/* No × in the header: the footer's Close is the same control, and two buttons
          both named "Close" would be indistinguishable to a screen reader. Escape and
          the scrim still close it too. */}
      <CardHeader
        // Dialog page, header with an icon: the red warning is decoration (the title
        // already names the dialog), so it takes no label.
        avatar={<WarningAmberIcon color="error" />}
        title={`${location.name} Can't Be Deleted`}
        slotProps={{
          // The avatar drops the title to body2, so it's set back to h3 like every dialog title.
          title: { id: titleId, component: 'h2', variant: 'h3' },
          avatar: { sx: { mr: 1.5, display: 'flex' } },
        }}
      />

      <DialogContent sx={{ p: 2 }}>
        <Typography id={descriptionId}>{getMessage(partnerCheckpoints)}</Typography>
      </DialogContent>

      {/* Right-aligned; full width on mobile, like any dialog footer (Dialog page). */}
      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ flex: { xs: 1, sm: 'initial' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CannotDeleteLocationDialog;
